<template>
  <div class="agentic-dashboard-wrapper">
    <ErrorPopup />
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="dashboard-header-left">
        <div class="dash-top-center">
          <input 
            type="text" 
            v-model="dashboardName" 
            :style="{ width: inputWidth + 'ch' }" 
            placeholder="untitled"
            @change="handleDashboardNameChange"
            class="dashboard-name-input"
          />
        </div>
      </div>
      <div class="dashboard-header-right">
        <button 
          class="chat-toggle-btn" 
          @click="toggleChat"
          :class="{ 'active': chatVisible }"
          :title="chatVisible ? 'Hide Chat' : 'Show Chat'"
        >
          <img :src="chatIcon" alt="chat" />
        </button>
        <button 
          class="dash-save" 
          @click="saveDashboard"
          :disabled="!isDashboardNameChanged || saving"
          :class="{ 'disabled': !isDashboardNameChanged || saving }"
        >
          <img :src="saveIcon" alt="save" />
        </button>
        <button class="dash-delete" @click="showDeleteDialog = true">
          <img :src="deleteIcon" alt="delete" />
        </button>
      </div>
    </div>

    <div class="agentic-dashboard-content">
      <!-- Main dashboard section with charts grid (6 slots) -->
      <div class="dashboard-main-section">
        <div class="dashboard-inner" style="position: relative;">
          <div v-if="loadingCharts" class="charts-loading-overlay">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
            <p class="loading-text">Loading plots...</p>
          </div>
          <DashboardGrid
            :charts="charts"
            @chart-action="handleChartAction"
            @empty-slot-click="handleEmptySlotClick"
          />
        </div>
      </div>
      
      <!-- Right sidebar with chat -->
      <div class="dashboard-sidebar" :class="{ 'hidden': !chatVisible }">
        <ChatSidebar
          ref="chatSidebar"
          :initial-message="initialMessage"
          :ai-response="aiResponse"
          @message-like="handleMessageLike"
          @message-dislike="handleMessageDislike"
          @send-message="handleChatMessage"
        />
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      transition="dialog-top-transition"
      class="save-dashboard"
      width="600"
      v-model="showDeleteDialog"
    >
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-title class="d-flex align-center justify-between">
            <v-spacer></v-spacer>
            <v-btn
              @click="showDeleteDialog = false"
              flat
              icon
              class="chart-preview-close-btn"
              size="x-small"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4 mx-5">
            <h6 class="text-center">Delete Dashboard</h6>
            <p class="text-center">
              Are you sure you want to delete dashboard?
            </p>
          </v-card-text>
          <v-card-actions class="btn-wrap">
            <v-btn class="cancel-btn" @click="isActive.value = false">
              <span>Cancel</span>
            </v-btn>
            <v-btn
              class="delete-btn"
              @click="confirmDelete"
              :loading="deleting"
            >
              <img class="mr-2" :src="deleteDashboardPopup" width="15px" height="15px" alt="delete_dashboard" />
              <span>Delete</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import DashboardGrid from "@/components/AgenticDashboard/DashboardGrid.vue";
import ChatSidebar from "@/components/AgenticDashboard/ChatSidebar.vue";
import ErrorPopup from "@/components/layout/Popups/ErrorPopup.vue";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import Plotly from "plotly.js-dist-min";

export default {
  name: "AgenticDashboardView",
  components: {
    DashboardGrid,
    ChatSidebar,
    ErrorPopup,
  },
  props: {
    dashboardId: {
      type: [String, Number],
      default: null,
    },
    dashboardName: {
      type: String,
      default: null,
    },
    initialMessage: {
      type: String,
      default: null,
    },
    dashboardData: {
      type: Object,
      default: null,
    },
  },
  data() {
    // Initialize dashboard name from URL if available (for refresh scenarios)
    const routeName = this.$route?.params?.dashboardName;
    let initialName = "Untitled Dashboard";
    if (routeName) {
      try {
        // Explicitly decode to convert %20 to spaces
        initialName = decodeURIComponent(routeName);
      } catch (e) {
        // If already decoded or invalid, use as is
        initialName = routeName;
      }
    }
    
    return {
      charts: Array(6).fill(null), // 6 slots for plots
      dashboardName: initialName,
      originalDashboardName: initialName, // Track original name to detect changes
      dashboardIdValue: null, // Store the actual dashboard ID
      chatVisible: true, // Chat sidebar visibility toggle
      showDeleteDialog: false,
      deleting: false,
      saving: false,
      deleteIcon: require("@/assets/Svg/Dashboard/delete.svg"),
      deleteDashboardPopup: require("@/assets/Svg/Chart/delete-white.svg"),
      saveIcon: require("@/assets/Svg/save.svg"),
      chatIcon: require("@/assets/Images/FDPal/send-2.svg"), // Chat toggle icon
      aiResponse: null,
      loadingCharts: false,
      loadingChatMessage: false, // Loading state for chat messages
      dashboardAccessLevel: "private", // Default access level
      isDefault: false, // Default dashboard flag
    };
  },
  computed: {
    tenantId() {
      return this.$route.path.split("/")[1] || getTenantPrefix();
    },
    inputWidth() {
      return this.dashboardName.length > 0 ? this.dashboardName.length : 15;
    },
    currentDashboardId() {
      // Get dashboard ID from stored value, prop, route params, or dashboardData
      if (this.dashboardIdValue) {
        return this.dashboardIdValue;
      }
      if (this.dashboardId) {
        return this.dashboardId;
      }
      if (this.$route.params.dashboardId) {
        return this.$route.params.dashboardId;
      }
      if (this.dashboardData && this.dashboardData.ID) {
        return this.dashboardData.ID;
      }
      return null;
    },
    isDashboardNameChanged() {
      // Check if dashboard name has changed from original
      return this.dashboardName.trim() !== this.originalDashboardName.trim() && 
             this.dashboardName.trim() !== "";
    },
  },
  mounted() {
    this.initializeDashboard();
  },
  watch: {
    // Watch for route changes (e.g., when user navigates to a different dashboard)
    '$route.params.dashboardName'(newName, oldName) {
      if (newName && newName !== oldName) {
        console.log("Dashboard name changed in route, loading dashboard:", newName);
        this.initializeDashboard();
      }
    },
    // Watch for dashboardName prop changes
    dashboardName(newName, oldName) {
      if (newName && newName !== oldName) {
        console.log("Dashboard name prop changed, loading dashboard:", newName);
        this.initializeDashboard();
      }
    },
    // Watch for dashboardId prop changes (backward compatibility)
    dashboardId(newId, oldId) {
      if (newId && newId !== oldId) {
        console.log("Dashboard ID prop changed, loading dashboard:", newId);
        this.initializeDashboard();
      }
    },
    // Watch for chat visibility changes and resize charts
    chatVisible(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Wait for CSS transition to complete (0.3s) before resizing
        setTimeout(() => {
          this.resizeAllCharts();
        }, 350); // Slightly longer than CSS transition (300ms)
      }
    },
  },
  methods: {
    initializeDashboard() {
      // Check if we have dashboard data from API response (from prop or query params)
      const dashboardData = this.dashboardData || (() => {
        const dashboardDataStr = this.$route.query.dashboardData;
        if (dashboardDataStr) {
          try {
            return JSON.parse(dashboardDataStr);
          } catch (error) {
            console.error("Error parsing dashboard data:", error);
            return null;
          }
        }
        return null;
      })();

      // Get dashboard name from URL route params FIRST (for refresh scenarios)
      // This ensures we always use the URL, not the input field value
      let dashboardName = this.$route.params.dashboardName || this.dashboardName;
      // Get dashboard ID from prop or route params (backward compatibility)
      const dashboardId = this.dashboardId || this.$route.params.dashboardId;

      if (dashboardData) {
        this.handleDashboardApiResponse(dashboardData);
      } else if (dashboardName) {
        // Ensure the name from URL is properly decoded
        if (this.$route.params.dashboardName) {
          // Name is from URL - ensure it's decoded (Vue Router should decode, but double-check)
          try {
            dashboardName = decodeURIComponent(this.$route.params.dashboardName);
          } catch (e) {
            // If already decoded or invalid, use as is
            dashboardName = this.$route.params.dashboardName;
          }
        } else {
          // Name is from prop or data, ensure it's decoded
          try {
            dashboardName = decodeURIComponent(dashboardName);
          } catch (e) {
            // Already decoded or not encoded
          }
        }
        console.log("Loading dashboard from name (from URL):", dashboardName);
        console.log("Route params dashboardName (raw):", this.$route.params.dashboardName);
        console.log("Current dashboardName in data:", this.dashboardName);
        // Use the name from URL, not from input field
        this.loadDashboardByName(dashboardName);
      } else if (dashboardId) {
        console.log("Loading dashboard from ID:", dashboardId);
        this.loadDashboard(dashboardId);
      } else if (this.initialMessage) {
        // If we have an initial message but no dashboard ID, create a new dashboard
        this.createDashboardFromMessage(this.initialMessage);
      }
    },
    async loadDashboardByName(name) {
      if (!name) {
        console.error("Dashboard name is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard name is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        // Load dashboard data by name
        // The API expects the name to be JSON-stringified (with quotes) and then URL encoded
        // Example: name="Untitled 88" -> JSON.stringify -> "\"Untitled 88\"" -> encodeURIComponent -> "%22Untitled%2088%22"
        const jsonStringifiedName = JSON.stringify(name);
        const encodedName = encodeURIComponent(jsonStringifiedName);
        console.log("Fetching dashboard with name:", name);
        console.log("JSON stringified:", jsonStringifiedName);
        console.log("URL encoded:", encodedName);
        console.log("Full API URL:", `public/api/dashboards?name=${encodedName}`);
        const response = await api.get(`public/api/dashboards?name=${encodedName}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const dashboards = response.data.data.dashboards || [];
          if (dashboards.length === 0) {
            throw new Error("Dashboard not found");
          }
          
          const dashboard = dashboards[0];
          const fetchedName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          
          // Store the actual dashboard ID
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          // Update dashboardName from API response (this will update the input field)
          this.dashboardName = fetchedName;
          this.originalDashboardName = fetchedName; // Set original name
          this.dashboardAccessLevel = dashboard.AccessLevel || "private";
          this.isDefault = dashboard.isDefault || false;

          // Update URL with dashboard name if it's different
          // Vue Router will handle encoding automatically
          const currentName = this.$route.params.dashboardName;
          if (currentName !== this.dashboardName) {
            this.$router.replace({
              name: "AgenticDashboardView",
              params: { dashboardName: this.dashboardName },
            });
          }

          // Load charts from ChartsID array (same as handleDashboardApiResponse)
          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            this.loadingCharts = true;
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.loadingCharts = false;
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            // Reset charts array if no charts
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async loadDashboard(id) {
      if (!id) {
        console.error("Dashboard ID is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard ID is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        // Load dashboard data by ID (backward compatibility)
        const response = await api.get(`public/api/dashboards/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const dashboard = response.data.data.dashboards[0];
          
          // Store the actual dashboard ID
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          this.dashboardName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          this.originalDashboardName = this.dashboardName; // Set original name
          this.dashboardAccessLevel = dashboard.AccessLevel || "private";
          this.isDefault = dashboard.isDefault || false;

          // Update URL with dashboard name
          // Vue Router will handle encoding automatically
          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName },
          });

          // Load charts from ChartsID array (same as handleDashboardApiResponse)
          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            this.loadingCharts = true;
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.loadingCharts = false;
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            // Reset charts array if no charts
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async handleDashboardApiResponse(dashboardData) {
      // Store the actual dashboard ID
      this.dashboardIdValue = dashboardData.ID || dashboardData.id || null;
      
      // Set dashboard name
      this.dashboardName = dashboardData.Name || "Untitled Dashboard";
      this.originalDashboardName = this.dashboardName; // Set original name
      this.dashboardAccessLevel = dashboardData.AccessLevel || "private";
      this.isDefault = dashboardData.isDefault || false;
      
      // Set AI response content
      if (dashboardData.content) {
        this.aiResponse = dashboardData.content;
      }

      // Load charts from ChartsID array
      if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
        this.loadingCharts = true;
        await this.loadChartsFromIds(dashboardData.ChartsID);
        this.loadingCharts = false;
      }
    },
    async loadChartsFromIds(chartIds) {
      // Reset charts array
      this.charts = Array(6).fill(null);
      
      // Load each chart by ID
      for (let i = 0; i < chartIds.length && i < 6; i++) {
        const chartId = chartIds[i];
        try {
          // First get chart metadata
          const chartResponse = await api.get(`public/api/charts/${chartId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          });

          if (
            chartResponse &&
            chartResponse.data &&
            (chartResponse.data.statusCode === 200 || chartResponse.data.statusCode === 201)
          ) {
            const chart = chartResponse.data.data?.chart || chartResponse.data.data;
            if (chart) {
              // Fetch chart data
              try {
                const chartDataResponse = await api.get(`public/api/charts/data/${chartId}`, {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    "X-TenantID": this.tenantId,
                  },
                });

                let chartData = null;
                if (
                  chartDataResponse &&
                  chartDataResponse.data &&
                  (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
                ) {
                  const samples = chartDataResponse.data.data?.samples;
                  
                  // Handle different sample formats
                  if (samples != null) {
                    if (Array.isArray(samples) && samples.length > 0) {
                      chartData = samples;
                    } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
                      chartData = samples.rows;
                    }
                  }
                }

                this.charts[i] = {
                  title: chart.Name || `Chart ${i + 1}`,
                  data: chart,
                  chartData: chartData,
                  index: i,
                };
              } catch (dataError) {
                console.error(`Error loading chart data for ${chartId}:`, dataError);
                // Still add chart even if data fails
                this.charts[i] = {
                  title: chart.Name || `Chart ${i + 1}`,
                  data: chart,
                  chartData: null,
                  index: i,
                };
              }
            }
          }
        } catch (error) {
          console.error(`Error loading chart ${chartId}:`, error);
        }
      }
    },
    createDashboardFromMessage(message) {
      // Create a new dashboard from the message
      // This will be handled by the backend API
      console.log("Creating dashboard from message:", message);
    },
    handleChartAction(index) {
      // Handle chart action
      console.log("Chart action triggered for chart:", index);
    },
    handleMessageLike(index) {
      // Handle message like
      console.log("Message liked:", index);
    },
    handleMessageDislike(index) {
      // Handle message dislike
      console.log("Message disliked:", index);
    },
    async handleChatMessage(message) {
      // Handle new chat message with dashboard_id
      console.log("New chat message:", message);
      console.log("Current dashboard ID:", this.currentDashboardId);
      
      // Call the LLM API with message and dashboard_id
      const llmUrl = process.env.VUE_APP_LLM_URL;
      if (!llmUrl) {
        console.error("VUE_APP_LLM_URL is not defined in .env");
        this.$store.commit('api/showSnackbar', {
          message: "LLM URL is not configured",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      this.loadingChatMessage = true;
      try {
        // Get current charts IDs from the charts array
        const chartsID = this.charts
          .filter(chart => chart && chart.data && chart.data.ID)
          .map(chart => chart.data.ID);

        // Build full dashboard data object
        const dashboardData = {
          ID: this.currentDashboardId,
          Name: this.dashboardName,
          ChartsID: chartsID,
          AccessLevel: this.dashboardAccessLevel,
          isDefault: this.isDefault,
        };

        const requestPayload = {
          message: message,
        };

        // Add full dashboard data instead of just dashboard_id
        if (this.currentDashboardId) {
          requestPayload.dashboard = dashboardData;
        }

        const response = await api.post(
          `${llmUrl}api/dashboard`,
          requestPayload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (response && response.data) {
          const dashboardData = response.data;
          
          // Update AI response
          if (dashboardData.content) {
            this.aiResponse = dashboardData.content;
          }

          // Update charts if new charts are provided
          if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
            this.loadingCharts = true;
            await this.loadChartsFromIds(dashboardData.ChartsID);
            this.loadingCharts = false;
            // Show success message when charts are updated
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard updated successfully",
              color: 'success',
              timeout: 3000
            });
          }

          // Update dashboard name if provided
          if (dashboardData.Name) {
            this.dashboardName = dashboardData.Name;
            this.originalDashboardName = dashboardData.Name; // Update original name
            // Update URL with new dashboard name
            // Vue Router will handle encoding automatically
            this.$router.replace({
              name: "AgenticDashboardView",
              params: { dashboardName: this.dashboardName },
            });
          }
        } else {
          console.error("Invalid response from API");
          this.$store.commit('api/showSnackbar', {
            message: "Failed to process message",
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error sending chat message:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to send message",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.loadingChatMessage = false;
      }
    },
    handleDashboardNameChange() {
      // Handle dashboard name change
      console.log("Dashboard name changed:", this.dashboardName);
      // Save button will be enabled automatically via isDashboardNameChanged computed property
    },
    toggleChat() {
      // Toggle chat sidebar visibility
      this.chatVisible = !this.chatVisible;
    },
    resizeAllCharts() {
      // Resize all Plotly charts when container dimensions change
      this.$nextTick(() => {
        // Find all chart containers and resize their Plotly plots
        const chartContainers = document.querySelectorAll('.chart-container');
        chartContainers.forEach((container) => {
          const plotElement = container.querySelector('.js-plotly-plot') || 
                             (container.classList.contains('js-plotly-plot') ? container : null);
          if (plotElement) {
            try {
              Plotly.Plots.resize(plotElement).catch((err) => {
                console.warn("Error resizing chart:", err);
              });
            } catch (error) {
              console.warn("Error resizing chart:", error);
            }
          }
        });
      });
    },
    handleEmptySlotClick() {
      // If chat is closed, open it
      if (!this.chatVisible) {
        this.chatVisible = true;
        // Wait for chat to open and then focus input
        this.$nextTick(() => {
          setTimeout(() => {
            this.focusChatInput();
          }, 350); // Wait for CSS transition
        });
      } else {
        // If chat is open, focus the input field
        this.focusChatInput();
      }
    },
    focusChatInput() {
      // Focus the chat input field
      if (this.$refs.chatSidebar) {
        this.$refs.chatSidebar.focusInput();
      }
    },
    async saveDashboard() {
      if (!this.currentDashboardId) {
        this.$store.commit('api/showSnackbar', {
          message: "Cannot save: Dashboard ID not found",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      if (!this.isDashboardNameChanged) {
        return; // No changes to save
      }

      this.saving = true;

      try {
        // Get current charts IDs from the charts array
        const chartsID = this.charts
          .filter(chart => chart && chart.data && chart.data.ID)
          .map(chart => chart.data.ID);

        const requestBody = {
          Data: {
            dashboard: {
              name: this.dashboardName.trim(),
              chartsID: chartsID,
              AccessLevel: this.dashboardAccessLevel,
              isDefault: this.isDefault,
            },
          },
        };

        const response = await api.put(
          `public/api/dashboards/${this.currentDashboardId}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          // Update original name to reflect saved state
          this.originalDashboardName = this.dashboardName.trim();
          
          // Update URL with new dashboard name
          // Vue Router will handle encoding automatically
          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName.trim() },
          });
          
          this.$store.commit('api/showSnackbar', {
            message: "Dashboard updated successfully",
            color: 'success',
            timeout: 3000
          });
        } else {
          const errorMessage = response?.data?.message || "Failed to update dashboard";
          console.error(errorMessage);
          this.$store.commit('api/showSnackbar', {
            message: errorMessage,
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error updating dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Error updating dashboard",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      if (!this.dashboardId) {
        // If no dashboard ID, just navigate back
        this.$router.push({
          name: "AgenticDashboard",
        });
        return;
      }

      this.deleting = true;
      api
        .delete(`public/api/dashboards/${this.dashboardId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard deleted successfully",
              color: 'success',
              timeout: 3000
            });
            // Navigate back to dashboard list
            this.$router.push({
              name: "AgenticDashboard",
            });
          } else {
            this.$store.commit('api/showSnackbar', {
              message: response?.data?.message || "Failed to delete dashboard",
              color: 'error',
              timeout: 5000
            });
          }
        })
        .catch((error) => {
          console.error("Error deleting dashboard:", error);
          this.$store.commit('api/showSnackbar', {
            message: error.response?.data?.message || "Error deleting dashboard",
            color: 'error',
            timeout: 5000
          });
        })
        .finally(() => {
          this.deleting = false;
          this.showDeleteDialog = false;
        });
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard.css';
</style>

